import type { FocusEvent, KeyboardEvent, MouseEvent } from 'react';
import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';

import { SuccessIcon, WarningBasicIcon } from '@genially/icons';

import type { BaseInputProps } from '../types';
import { Size, Status } from '../types';
import {
  InputWrapper,
  RightIconWrapper,
  StyledInput,
  StyledSmallParagraph,
  Wrapper,
} from '../Common.styled';

export interface InternalBaseInputProps extends BaseInputProps {
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  readOnly?: boolean;
  className?: string;
  width?: string;
  type?: string;
  pattern?: string;
  hasEllipsis?: boolean;
  showComplementaryInfoInitially?: boolean;
}

const BaseInput = forwardRef<HTMLInputElement, InternalBaseInputProps>(
  (
    {
      id,
      className,
      size = Size.MEDIUM,
      placeholder,
      info,
      type = 'text',
      status = Status.DEFAULT,
      disabled = false,
      readOnly = false,
      dataCy,
      name,
      value,
      RightIcon,
      pattern,
      maxLength,
      minLength,
      autofocus = false,
      onFocus,
      onBlur,
      onKeyDown,
      onRightIconClick,
      onTouchStart,
      width,
      hasEllipsis = true,
      showComplementaryInfoInitially = false,
      onChange,
      blurOnEnter = true,
      autoComplete,
      'aria-label': ariaLabel,
      'aria-controls': ariaControls,
      'aria-haspopup': ariaHasPopup,
      'aria-expanded': ariaExpanded,
      forceFontSize,
      role,
    },
    ref,
  ) => {
    const [showInfo, setShowInfo] = useState(false);
    const [isScrolling, setIsScrolling] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [hasInfoAnimation, setHasInfoAnimation] = useState(
      status !== Status.DEFAULT && !disabled,
    );
    const internalRef = useRef(null);

    const currentRef = ref || internalRef;

    const getDefaultRightIcon = () => {
      switch (status) {
        case Status.SUCCESS:
          return <SuccessIcon />;
        case Status.ERROR:
          return <WarningBasicIcon />;
        default:
          return undefined;
      }
    };

    const getCurrentFromRef = useCallback(() => {
      if (typeof currentRef === 'function' || !currentRef) {
        return null;
      }

      return currentRef.current;
    }, [currentRef]);

    const handleTouchOutside = useCallback(
      (event: TouchEvent) => {
        const current = getCurrentFromRef();

        if (!(event.target instanceof Node) || !current) {
          return;
        }

        if (event.target && !current?.contains(event.target)) {
          setIsScrolling(false);

          if (!isFocused) {
            current.scrollLeft = 0;
          }
        }
      },
      [getCurrentFromRef, isFocused],
    );

    useEffect(() => {
      document.addEventListener('touchstart', handleTouchOutside, true);
      return () => {
        document.removeEventListener('touchstart', handleTouchOutside, true);
      };
    }, [handleTouchOutside]);

    const RightIconComponent = RightIcon || getDefaultRightIcon();

    const showInfoText =
      status === Status.DEFAULT || disabled
        ? showInfo || showComplementaryInfoInitially
        : true;

    const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
      setShowInfo(true);
      setIsFocused(true);
      setHasInfoAnimation(true);

      onFocus?.(event);
    };

    const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
      setShowInfo(false);
      setIsFocused(false);
      setHasInfoAnimation(true);

      onBlur?.(event);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && blurOnEnter) {
        getCurrentFromRef()?.blur();
      }

      onKeyDown?.(event);
    };

    const startScroll = () => {
      setIsScrolling(true);
    };

    const handleTouchMove = () => startScroll();
    const handleWheel = () => startScroll();

    const endScroll = () => {
      setIsScrolling(false);
    };

    const handleMouseOut = (
      e: React.TouchEvent<HTMLInputElement> | React.MouseEvent<HTMLInputElement>,
    ) => {
      endScroll();

      if (!isFocused && !showComplementaryInfoInitially) {
        e.currentTarget.scrollLeft = 0;
      }
    };

    const handleRightIconClick = (e: MouseEvent<HTMLInputElement>) => {
      if (!disabled) {
        onRightIconClick?.(e);
      }
    };

    const handleInputWrapperTouchStart = (event: React.TouchEvent) => {
      onTouchStart?.(event);
    };

    return (
      <Wrapper className={className} $width={width}>
        <InputWrapper
          $status={status}
          $disabled={disabled}
          $size={size}
          $hasIcon={!!RightIconComponent}
          onTouchStart={handleInputWrapperTouchStart}
        >
          <StyledInput
            id={id}
            ref={currentRef}
            data-cy={dataCy}
            as="input"
            autoFocus={autofocus}
            name={name}
            value={value}
            type={type}
            placeholder={placeholder}
            maxLength={maxLength}
            minLength={minLength}
            $status={status}
            disabled={disabled}
            pattern={pattern}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={onChange}
            readOnly={readOnly}
            onKeyDown={handleKeyDown}
            $isScrolling={isScrolling}
            onTouchMove={handleTouchMove}
            onWheel={handleWheel}
            onMouseOut={handleMouseOut}
            $hasEllipsis={hasEllipsis}
            form="novalidatedform"
            autoComplete={autoComplete}
            aria-label={ariaLabel}
            $forceFontSize={forceFontSize}
            role={role}
            aria-controls={ariaControls}
            aria-haspopup={ariaHasPopup}
            aria-expanded={ariaExpanded}
          />
          {RightIconComponent && (
            <RightIconWrapper
              $size={size}
              $status={status}
              $isClickable={!!onRightIconClick}
              $disabled={disabled}
              onMouseDown={handleRightIconClick}
            >
              {RightIconComponent}
            </RightIconWrapper>
          )}
        </InputWrapper>
        {info && (
          <StyledSmallParagraph
            $status={status}
            $disabled={disabled}
            $visible={showInfoText}
            $hasAnimation={hasInfoAnimation}
          >
            {info}
          </StyledSmallParagraph>
        )}
      </Wrapper>
    );
  },
);

export default BaseInput;
